import React, { useState, useRef, useEffect } from "react";
import SC from "./style";
import InnoIcons from "@InnoIcons/";
import { InputField } from "@components/";
import { Radio, RadioGroup, FormControlLabel } from "@mui/material";

const CreatedOnDropdown = ({
  label = "Created On",
  field = "createdOn",
  value = null, // current filter value
  onUpdate, // callback to update parent filter
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("withinLast");
  const [unit, setUnit] = useState("d");
  const [values, setValues] = useState({
    withinLast: "",
    moreThan: "",
    inRangeFrom: "",
    inRangeTo: "",
  });

  const dropdownRef = useRef();

  const radioOptions = [
    { key: "withinLast", label: "Within the last" },
    { key: "moreThan", label: "More than" },
    { key: "inRange", label: "Between" },
  ];

  // Initialize dropdown state from parent value
  useEffect(() => {
    if (!value) {
      // Clear state if value is null
      setSelectedOption("withinLast");
      setUnit("d");
      setValues({
        withinLast: "",
        moreThan: "",
        inRangeFrom: "",
        inRangeTo: "",
      });
      return;
    }

    const { header, value: val } = value;

    if (header === "inRange" && Array.isArray(val)) {
      setSelectedOption("inRange");
      setValues({
        withinLast: "",
        moreThan: "",
        inRangeFrom: val[0],
        inRangeTo: val[1],
      });
    } else if ((header === "withinLast" || header === "moreThan") && val) {
      setSelectedOption(header);
      setValues({ ...values, [header]: val.number || "" });
      setUnit(val.unit || "d");
    }
  }, [value]);

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleValueChange = (key, val) =>
    setValues((prev) => ({ ...prev, [key]: val }));

  const renderUnitSelect = () => (
    <SC.UnitSelect value={unit} onChange={(e) => setUnit(e.target.value)}>
      <option value="d">Days</option>
      <option value="h">Hours</option>
      <option value="m">Minutes</option>
      <option value="s">Seconds</option>
    </SC.UnitSelect>
  );

  const handleUpdate = () => {
    const { withinLast, moreThan, inRangeFrom, inRangeTo } = values;
    let searchObject = null;

    switch (selectedOption) {
      case "withinLast":
        if (withinLast)
          searchObject = {
            header: "withinLast",
            value: { number: withinLast, unit },
          };
        break;
      case "moreThan":
        if (moreThan)
          searchObject = {
            header: "moreThan",
            value: { number: moreThan, unit },
          };
        break;
      case "inRange":
        if (inRangeFrom && inRangeTo)
          searchObject = { header: "inRange", value: [inRangeFrom, inRangeTo] };
        break;
      default:
        break;
    }

    // Send filter to parent (null if empty)
    onUpdate?.(searchObject || null);
    setIsOpen(false);
  };

  const unitLabel = (unit) => {
    switch (unit) {
      case "d":
        return "days";
      case "h":
        return "hours";
      case "m":
        return "minutes";
      case "s":
        return "seconds";
      default:
        return unit;
    }
  };

  const hasValidValue = (val) => {
    if (!val) return false;
    switch (val.header) {
      case "withinLast":
      case "moreThan":
        return Boolean(val.value?.number);
      case "inRange":
        return Boolean(val.value?.[0] && val.value?.[1]);
      default:
        return false;
    }
  };

  return (
    <SC.DropdownContainer ref={dropdownRef}>
      <SC.DropdownButton
        isOpen={isOpen}
        isActive={hasValidValue(value)}
        onClick={() => setIsOpen(!isOpen)}
      >
        {hasValidValue(value)
          ? (() => {
              if (value.header === "inRange")
                return `${label}: ${value.value[0]} to ${value.value[1]}`;
              if (value.header === "withinLast")
                return `${label}: Within the last ${
                  value.value.number
                } ${unitLabel(value.value.unit)}`;
              if (value.header === "moreThan")
                return `${label}: More than ${value.value.number} ${unitLabel(
                  value.value.unit
                )} ago`;
              return label;
            })()
          : label}
        <SC.ChevronIcon
          size={14}
          isOpen={isOpen}
          isActive={hasValidValue(value)}
        />
      </SC.DropdownButton>

      {isOpen && (
        <SC.DropdownMenu isOpen={isOpen}>
          <SC.OptionGroup>
            <RadioGroup
              name={field}
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
            >
              {radioOptions.map(({ key, label }) => (
                <div key={key}>
                  <FormControlLabel
                    value={key}
                    control={
                      <Radio
                        sx={{
                          "& .MuiSvgIcon-root": { fontSize: 20 },
                          "&.Mui-checked": { color: "#EC810B" },
                        }}
                      />
                    }
                    label={<span style={{ fontSize: "14px" }}>{label}</span>}
                  />
                  {selectedOption === key && (
                    <SC.InputRow>
                      {key === "inRange" ? (
                        <>
                          <SC.DateInput
                            type="date"
                            value={values.inRangeFrom}
                            onChange={(e) =>
                              handleValueChange("inRangeFrom", e.target.value)
                            }
                          />
                          <SC.DateSeparator>to</SC.DateSeparator>
                          <SC.DateInput
                            type="date"
                            value={values.inRangeTo}
                            onChange={(e) =>
                              handleValueChange("inRangeTo", e.target.value)
                            }
                          />
                        </>
                      ) : (
                        <>
                          <InputField
                            name={key}
                            type="number"
                            value={values[key]}
                            onChange={(name, val) =>
                              handleValueChange(key, val)
                            }
                            style={{
                              width: "30%",
                              height: "34px",
                              borderRadius: "5px",
                            }}
                          />
                          {renderUnitSelect()}
                        </>
                      )}
                    </SC.InputRow>
                  )}
                </div>
              ))}
            </RadioGroup>
          </SC.OptionGroup>

          <SC.DropdownFooter>
            <SC.UpdateButton onClick={handleUpdate}>Update</SC.UpdateButton>

            <SC.ClearButton
              onClick={() => {
                setSelectedOption("withinLast");
                setUnit("d");
                setValues({
                  withinLast: "",
                  moreThan: "",
                  inRangeFrom: "",
                  inRangeTo: "",
                });
                onUpdate?.(null);
                setIsOpen(false);
              }}
            >
              Clear
            </SC.ClearButton>
          </SC.DropdownFooter>
        </SC.DropdownMenu>
      )}
    </SC.DropdownContainer>
  );
};

export default CreatedOnDropdown;
